import cn from 'classnames';
import { uniq } from 'lodash-es';
import { FC } from 'react';
import { useSearchParams } from 'react-router';

import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { scrollToElementByID } from '@/shared/lib';

import {
  IOXRayImagesInterfaceModel,
  IOXrayImageInterface,
  groupIOXRayImagesByPartition,
} from '@/entities/IOXRayImagesMatrix';
import { organizationModel } from '@/entities/organization';
import {
  FMXNavigationMode,
  is2DReportRightColumnScrollable,
  reportsModel,
} from '@/entities/reports';
import { toothModel } from '@/entities/tooth';

import { RenderViewSlider } from '@/features/renderViewSlider';

import styles from './PanowingImageSlider.module.scss';

type PanowingImageSliderProps = {
  className?: string;
};

export const PanowingImageSlider: FC<PanowingImageSliderProps> = (props) => {
  const { className } = props;
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentReport = useAppSelector(
    reportsModel.selectors.selectCurrentReport,
  );

  const navigationMode = useAppSelector(
    reportsModel.selectors.selectNavigationMode,
  );

  const reportID = currentReport?.ID as string;

  const { isFDAVersion2 } = useAppSelector(
    organizationModel.selectors.selectFDAEnvironment,
  );

  const IOXRayImagesInterface = useAppSelector(
    IOXRayImagesInterfaceModel.selectors.selectIOXRayImagesInterfaceByReportID,
  );

  const panoImageInterface = useAppSelector(
    IOXRayImagesInterfaceModel.selectors.selectPanoImageInterfaceByReportID,
  ) as IOXrayImageInterface;

  const groupedIOXRayImages = groupIOXRayImagesByPartition(
    IOXRayImagesInterface as IOXrayImageInterface[],
  );

  const bitewingsImagesInterface = [
    ...groupedIOXRayImages['MiddleLeft'],
    ...groupedIOXRayImages['MiddleRight'],
  ];

  const bitewingsImageMetaIDs = uniq(
    bitewingsImagesInterface.map(
      (imageItem) =>
        imageItem.imageMeta?.GeneratedAssetID ||
        imageItem?.imageMeta?.StudyAssetID,
    ),
  );

  const panoImageMetaID = panoImageInterface
    ? panoImageInterface.imageMeta?.GeneratedAssetID ||
      panoImageInterface?.imageMeta?.StudyAssetID
    : '';

  const resultImageMetaIDs = panoImageInterface
    ? [panoImageMetaID, ...bitewingsImageMetaIDs]
    : [];

  const focusedImageMetaID = useAppSelector(
    reportsModel.selectors.selectFocusedImageMetaID,
  );

  const teethIDs = useAppSelector(
    toothModel.selectors.selectTeethIDsToShow(
      reportID as string,
      isFDAVersion2,
    ),
  );
  const activeToothID = useAppSelector(
    reportsModel.selectors.selectActiveToothID,
  );

  const isFMXMatrixViewMode = navigationMode === FMXNavigationMode.MatrixView;

  const itemIDs = isFMXMatrixViewMode ? resultImageMetaIDs : teethIDs;
  const activeItemID = isFMXMatrixViewMode ? focusedImageMetaID : activeToothID;

  const updateActiveToothID = (newItemID: string) => {
    dispatch(reportsModel.actions.setToolbarActiveControl('view'));

    if (isFMXMatrixViewMode) {
      dispatch(reportsModel.actions.setFocusedMetaImageID(newItemID));

      // Update URL with new activeItemID only for bitewings images
      if (newItemID && newItemID !== panoImageMetaID) {
        searchParams.set('activeItemID', newItemID);
      } else {
        searchParams.delete('activeItemID');
      }
      setSearchParams(searchParams);
    } else {
      dispatch(reportsModel.actions.setActiveToothID(newItemID));

      // NOTE: It needs to properly scroll to the tooth card which appears in the DOM later than this function executes if it's just a sync call.
      if (is2DReportRightColumnScrollable()) {
        setTimeout(() => scrollToElementByID(newItemID, 'smooth', 'start'), 0);
      }

      if (newItemID === '') {
        dispatch(
          reportsModel.actions.setNavigationMode(FMXNavigationMode.MatrixView),
        );
        dispatch(reportsModel.actions.setFocusedMetaImageID(newItemID));

        // Remove activeItemID parameter from URL when selection is reset
        searchParams.delete('activeItemID');
        setSearchParams(searchParams);
      }
    }
  };

  return (
    <RenderViewSlider
      updateActiveItem={updateActiveToothID}
      itemIDs={itemIDs as string[]}
      activeItemID={activeItemID}
      className={cn(styles.container, className)}
    />
  );
};
